import React from 'react';
import { useLocation } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import { HttpStatus } from 'v2/interfaces/http';
import { ResolveSubscriptionChangeStatus } from 'v2/interfaces/subscriptions';
import type { ResolveSubscriptionChangeResponse } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

import { Topbar } from '@eduzz/ui-layout';
import { Icon } from '@eduzz/ui-antd-theme';

type MessageType = {
  icon: React.ReactNode;
  description: string;
};


export default function ResolveRecurringDateChange() {
  return (
    <div>
      <Topbar />
      <ResolveRecurringDateChangeResultSection />
    </div>
  );
}

function ResolveRecurringDateChangeResultSection() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const token = params.get('token');
  const action = params.get('action');

  const response = useObservable(() => {
    return subscriptionsService.resolveRecurringDateChange(token, action);
  }, []);

  const [body, error] = response;

  if (!body && !error) {
    return <GlobalLoading />;
  }

  if (error?.response?.status === HttpStatus.UnprocessableEntity) {
    return (
      <MessageContainer
        icon={<Icon.TriangleAlert size={72} className='text-[4.5rem] text-(--uizz-antd-color-warning)'  />}
        description='Este link já foi utilizado portanto não é mais válido.'
      />
    );
  }

  if (error) {
    return (
      <MessageContainer
        icon={<Icon.TriangleAlert size={72} className='text-[4.5rem] text-(--uizz-antd-color-warning)'  />}
        description='Ocorreu um erro ao processar a solicitação. Por favor, tente novamente mais tarde.'
      />
    );
  }

  const messageMapper: Record<ResolveSubscriptionChangeResponse['data']['status'], MessageType> = {
    [ResolveSubscriptionChangeStatus.EXPIRED]: {
      icon: <Icon.TriangleAlert size={72} className='text-[4.5rem] text-(--uizz-antd-color-warning)'  />,
      description:
        'Este link expirou. O prazo de resposta de 48 horas foi ultrapassado. Para alterar a data de vencimento, entre em contato com o produtor.'
    },
    [ResolveSubscriptionChangeStatus.APPROVED]: {
      icon: <Icon.CircleCheck size={72} className='text-[4.5rem] text-(--uizz-antd-color-success)'  />,
      description:
        'Solicitação confirmada! A nova data de vencimento será atualizada no seu contrato conforme solicitado.'
    },
    [ResolveSubscriptionChangeStatus.REJECTED]: {
      icon: <Icon.CircleX size={72} className='text-[4.5rem] text-(--uizz-antd-color-error)'  />,
      description: 'Alteração recusada. Sua data de vencimento continuará a mesma.'
    }
  };

  const message = messageMapper[body.data.status] || {
    icon: <Icon.TriangleAlert size={72} className='text-[4.5rem] text-(--uizz-antd-color-warning)'  />,
    description: 'Algo deu errado. Por favor, tente novamente mais tarde.'
  };

  return <MessageContainer icon={message.icon} description={message.description} />;
}

function MessageContainer({ icon, description }: MessageType) {
  return (
    <div className='flex h-[calc(100vh-64px)] w-full flex-col items-center justify-center p-4 text-2xl'>
      <div className='flex w-full max-w-125 flex-col items-center justify-center text-center'>
        <span>{icon}</span>
        <span>{description}</span>
      </div>
    </div>
  );
}
